import axios from 'axios';
import { AES, enc } from 'crypto-js';

let timeOffset: number | null = null;
let isSyncing = false;
let syncPromise: Promise<void> | null = null;

interface ServerTimeResponse {
	serverTime: number;
	timestamp: string;
}

/**
 * Fetches server time and calculates the offset between server and client
 */
export const syncServerTime = async (): Promise<void> => {
	// Prevent multiple simultaneous sync requests
	if (isSyncing && syncPromise) {
		return syncPromise;
	}

	isSyncing = true;
	syncPromise = (async () => {
		try {
			const clientTimeBeforeRequest = Date.now();

			const response = await axios.get<ServerTimeResponse>(
				`${import.meta.env.VITE_COMMON_URL}/common/ref-sync`,
			);

			const clientTimeAfterRequest = Date.now();
			const serverTime = response.data.serverTime;

			// Calculate the estimated time when server sent the response
			// (average of before and after request times)
			const estimatedClientTime =
				(clientTimeBeforeRequest + clientTimeAfterRequest) / 2;

			// Calculate offset: positive if server is ahead, negative if behind
			timeOffset = serverTime - estimatedClientTime;

			console.log(`Server time synchronized. Offset: ${timeOffset}ms`);
		} catch (error) {
			console.error('Failed to sync server time:', error);
			// Fallback: assume no offset if sync fails
			timeOffset = 0;
		} finally {
			isSyncing = false;
			syncPromise = null;
		}
	})();

	return syncPromise;
};

/**
 * Gets the current time adjusted for server-client offset
 */
const getSynchronizedTime = (): number => {
	if (timeOffset === null) {
		console.warn('Time not synchronized with server. Using client time.');
		return Date.now();
	}
	return Date.now() + timeOffset;
};

/**
 * Generates encrypted request header with server-synchronized time
 * Automatically syncs with server if not already synchronized
 */
export const generateRequestHeader = async (
	expirationMinutes = 5,
): Promise<string> => {
	// Sync with server if not already done
	if (timeOffset === null) {
		await syncServerTime();
	}

	const synchronizedNow = getSynchronizedTime();
	const expirationTime = synchronizedNow + expirationMinutes * 60 * 1000;

	const encrypted = AES.encrypt(
		JSON.stringify(expirationTime),
		import.meta.env.VITE_EXPIRATION_TIME_SECRET,
	).toString();

	const encoded = enc.Utf8.parse(encrypted).toString(enc.Base64);

	return encoded;
};

/**
 * Resets the time offset (useful for re-syncing)
 */
export const resetTimeSync = (): void => {
	timeOffset = null;
};
